import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import api, { formatApiError } from "../lib/api";
import { Home, User, Mail, Phone, Lock, Building2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { propertyTypes } from "../lib/i18n";
import GoogleSignInButton from "../components/GoogleSignInButton";

export default function Register() {
  const { t, lang } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [locations, setLocations] = useState({});
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    property_name: "",
    property_type: "chalet",
    governorate: "",
    wilayat: "",
    village: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/oman/locations")
      .then((r) => {
        setLocations(r.data);
        setLocationsLoaded(true);
      })
      .catch((e) => {
        setLocationsLoaded(true);
        toast.error(formatApiError(e));
      });
  }, []);

  const wilayatsList = form.governorate ? locations[form.governorate] : null;
  const wilayatsEn = wilayatsList?.wilayats || [];
  const wilayats = wilayatsList
    ? (lang === "ar" ? (wilayatsList.wilayats_ar || wilayatsList.wilayats) : wilayatsList.wilayats)
    : [];

  // Handle governorate change: auto-select first wilayat for smoother UX
  const onGovernorateChange = (e) => {
    const gov = e.target.value;
    const firstWilayat = locations[gov]?.wilayats?.[0] || "";
    setForm({ ...form, governorate: gov, wilayat: firstWilayat });
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    for (const k of ["name", "email", "phone", "password", "property_type", "governorate", "wilayat"]) {
      if (!form[k]) return setError(t("fields_required"));
    }
    setLoading(true);
    try {
      await register(form);
      toast.success(t("saved"));
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err));
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const placeholderGov = t("select_governorate");
  const placeholderWilayat = t("select_wilayat");
  const govOrder = Object.keys(locations);

  return (
    <div className="min-h-screen grain-bg flex flex-col">
      <header className="max-w-7xl w-full mx-auto flex items-center justify-between px-4 py-4">
        <Link to="/" className="flex items-center gap-2 font-semibold text-[#2A5948]">
          <Home size={18} />
          <span>{t("brand")}</span>
        </Link>
        <LanguageSwitcher />
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-2xl bg-white border border-[#E5E1D8] rounded-2xl p-8 shadow-sm">
          <h1 className="text-2xl font-semibold">{t("register_title")}</h1>
          <p className="text-sm text-[#5C6053] mt-1">{t("register_sub")}</p>

          <div className="mt-6">
            <GoogleSignInButton testid="register-google-btn" />
          </div>
          <div className="my-5 flex items-center gap-3 text-xs text-[#5C6053]">
            <div className="h-px flex-1 bg-[#E5E1D8]" />
            <span>{t("or")}</span>
            <div className="h-px flex-1 bg-[#E5E1D8]" />
          </div>

          <form className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-4" onSubmit={submit}>
            <Field label={t("name")} icon={User}>
              <input data-testid="reg-name-input" value={form.name} onChange={update("name")} className="w-full bg-transparent outline-none text-sm" />
            </Field>
            <Field label={t("email")} icon={Mail}>
              <input data-testid="reg-email-input" type="email" value={form.email} onChange={update("email")} dir="ltr" className="w-full bg-transparent outline-none text-sm" />
            </Field>
            <Field label={t("phone")} icon={Phone}>
              <input data-testid="reg-phone-input" type="tel" value={form.phone} onChange={update("phone")} dir="ltr" placeholder="+9689XXXXXXXX" className="w-full bg-transparent outline-none text-sm" />
            </Field>
            <Field label={t("password")} icon={Lock}>
              <input data-testid="reg-password-input" type="password" value={form.password} onChange={update("password")} className="w-full bg-transparent outline-none text-sm" />
            </Field>

            <div className="sm:col-span-2 h-px bg-[#E5E1D8] my-2" />

            <Field label={t("property_name")} icon={Building2}>
              <input data-testid="reg-property-name-input" value={form.property_name} onChange={update("property_name")} className="w-full bg-transparent outline-none text-sm" />
            </Field>
            <Field label={t("property_type")} icon={Building2}>
              <select
                data-testid="reg-property-type-select"
                value={form.property_type}
                onChange={update("property_type")}
                className="w-full bg-transparent outline-none text-sm"
              >
                {propertyTypes.map((pt) => (
                  <option key={pt} value={pt}>{t(`type_${pt}`)}</option>
                ))}
              </select>
            </Field>

            <Field label={t("governorate")} icon={MapPin}>
              <select
                data-testid="reg-governorate-select"
                value={form.governorate}
                onChange={onGovernorateChange}
                className="w-full bg-transparent outline-none text-sm"
                disabled={!locationsLoaded}
              >
                <option value="">{locationsLoaded ? placeholderGov : "..."}</option>
                {govOrder.map((k) => (
                  <option key={k} value={k}>{lang === "ar" ? locations[k].ar : k}</option>
                ))}
              </select>
            </Field>

            <Field label={t("wilayat")} icon={MapPin}>
              <select
                data-testid="reg-wilayat-select"
                value={form.wilayat}
                onChange={update("wilayat")}
                className="w-full bg-transparent outline-none text-sm disabled:opacity-50"
                disabled={!form.governorate}
              >
                <option value="">{placeholderWilayat}</option>
                {wilayatsEn.map((w, i) => (
                  <option key={w} value={w}>{wilayats[i] || w}</option>
                ))}
              </select>
            </Field>

            <div className="sm:col-span-2">
              <Field label={t("village")} icon={MapPin}>
                <input data-testid="reg-village-input" value={form.village} onChange={update("village")} className="w-full bg-transparent outline-none text-sm" />
              </Field>
            </div>

            {error && (
              <div data-testid="register-error" className="sm:col-span-2 text-sm text-[#C96A52] bg-[#FBE9E4] border border-[#F1C9BF] rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="register-submit-btn"
              disabled={loading}
              className="sm:col-span-2 brand-btn py-3 rounded-xl font-medium disabled:opacity-60"
            >
              {loading ? t("creating_account") : t("hero_cta")}
            </button>
          </form>

          <div className="mt-6 text-sm text-center text-[#5C6053]">
            {t("have_account")}{" "}
            <Link data-testid="link-to-login" to="/login" className="text-[#2A5948] font-medium">
              {t("login")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-[#5C6053] mb-1">{label}</div>
      <div className="flex items-center gap-2 border border-[#E5E1D8] bg-[#F9F6F0] rounded-xl px-3 py-2.5 focus-within:ring-2 focus-within:ring-[#2A5948]/40">
        {Icon && <Icon size={16} className="text-[#5C6053]" />}
        {children}
      </div>
    </label>
  );
}
